import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/images/logo.svg';

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 820);
    const [activeLink, setActiveLink] = useState(null);
    const location = useLocation();

    // Primary brand color
    const primaryColor = '#163ba2';
    const primaryHoverColor = '#0f2875';

    // Text colors based on background
    const textColorLight = '#ffffff';
    const textColorDark = '#2c3e50';

    // Set active link based on current route

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    
    useEffect(() => {
        setActiveLink(location.pathname);
    }, [location]);

    // Track scroll position to change header appearance
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 30);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 820);
            if (window.innerWidth > 820) {
                setMobileMenuOpen(false);
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close mobile menu when changing routes
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location]);

    // Base styles
    const headerStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: scrolled ? '8px 5%' : '15px 5%',
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
        boxShadow: scrolled ? '0 4px 20px rgba(22, 59, 162, 0.12)' : 'none',
        transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
        backdropFilter: 'blur(10px)',
        borderBottom: scrolled ? '1px solid rgba(22, 59, 162, 0.1)' : 'none',
        borderRadius: scrolled ? '0 0 10px 10px' : '0',
    };

    const logoContainerStyle = {
        display: 'flex',
        alignItems: 'start',
        zIndex: 1001
    };

    const logoStyle = {
        height: scrolled ? '40px' : '50px',
        transition: 'height 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)',
    };

    const navContainerStyle = {
        display: isMobile ? 'none' : 'flex',
        flex: 1,
        justifyContent: 'center'
    };

    const navStyle = {
        display: 'flex',
        gap: '35px',
        alignItems: 'center',
        transition: 'all 0.3s ease'
    };

    const ctaContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        marginLeft: '20px',
        zIndex: 1001
    };

    const mobileNavStyle = {
        display: mobileMenuOpen ? 'flex' : 'none',
        flexDirection: 'column',
        position: 'fixed',
        top: headerStyle.padding === '8px 5%' ? '60px' : '70px',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        padding: '30px 5%',
        boxShadow: '0 10px 25px rgba(22, 59, 162, 0.15)',
        alignItems: 'center',
        gap: '18px',
        zIndex: 999,
        opacity: mobileMenuOpen ? 1 : 0,
        transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease'
    };

    const linkStyle = (isActive) => ({
        textDecoration: 'none',
        color: isActive 
            ? primaryColor 
            : scrolled 
                ? textColorDark 
                : textColorLight,
        fontWeight: isActive ? 700 : 500,
        position: 'relative',
        padding: '5px 2px',
        fontSize: '15px',
        letterSpacing: '0.4px',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: 'Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        overflow: 'hidden'
    });

    const activeLinkIndicator = {
        position: 'absolute',
        bottom: '-2px',
        left: '0',
        width: '100%',
        height: '2px',
        backgroundColor: primaryColor,
        transformOrigin: 'center',
        animation: 'scaleIn 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96) forwards',
        transform: 'scaleX(1)'
    };

    // Define a keyframe animation in a style tag in your component
    const keyframesStyle = `
        @keyframes scaleIn {
            0% { transform: scaleX(0); }
            100% { transform: scaleX(1); }
        }
    `;

    const buttonStyle = {
        padding: '10px 24px',
        backgroundColor: scrolled ? primaryColor : 'rgba(255, 255, 255, 0.15)',
        color: scrolled ? 'white' : 'white',
        border: scrolled ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '600',
        letterSpacing: '0.5px',
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        boxShadow: scrolled 
            ? `0 4px 10px rgba(22, 59, 162, 0.28)`
            : '0 4px 10px rgba(0, 0, 0, 0.15)',
        fontFamily: 'Segoe UI, Roboto, Helvetica, Arial, sans-serif'
    };

    const hamburgerStyle = {
        display: isMobile ? 'flex' : 'none',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '26px',
        height: '20px',
        cursor: 'pointer',
        zIndex: 1001,
        marginLeft: '15px'
    };

    const hamburgerLineStyle = (position) => {
        const baseStyle = {
            width: '100%',
            height: '2px',
            backgroundColor: scrolled ? textColorDark : textColorLight,
            borderRadius: '4px',
            transition: 'all 0.3s cubic-bezier(0.68, -0.6, 0.32, 1.6)'
        };

        if (mobileMenuOpen) {
            if (position === 'top') {
                return {
                    ...baseStyle,
                    transform: 'rotate(45deg) translate(5px, 6px)',
                    backgroundColor: primaryColor
                };
            } else if (position === 'middle') {
                return {
                    ...baseStyle,
                    opacity: 0,
                    transform: 'translateX(-10px)'
                };
            } else {
                return {
                    ...baseStyle,
                    transform: 'rotate(-45deg) translate(5px, -6px)',
                    backgroundColor: primaryColor
                };
            }
        }
        
        return baseStyle;
    };

    // Hover animations
    const handleLinkHover = (e, isActive) => {
        e.target.style.color = primaryColor;
        e.target.style.transform = 'translateY(-2px)';
    };

    const handleLinkLeave = (e, isActive) => {
        if (isActive) {
            e.target.style.color = primaryColor;
        } else {
            e.target.style.color = scrolled ? textColorDark : textColorLight;
        }
        e.target.style.transform = 'translateY(0)';
    };

    // Enhanced click animation with immediate indicator display
    const handleLinkClick = (e, path) => {
        // Apply a flash pulse animation on click
        e.target.style.transform = 'scale(0.95)';
        e.target.style.transition = 'transform 0.1s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        
        // Return to normal state
        setTimeout(() => {
            e.target.style.transform = 'scale(1)';
        }, 200);
        
        // Pre-set the active link for smoother transition
        setActiveLink(path);
    };

    // Check if a link should be marked as active
    const isLinkActive = (path) => {
        return activeLink === path || location.pathname === path;
    };

    return (
        <>
            <style>{keyframesStyle}</style>
            <header style={headerStyle}>
                <div style={logoContainerStyle}>
                    <Link to="/">
                        <img 
                            src={logo} 
                            alt="Deecee Elevators" 
                            style={logoStyle} 
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        />
                    </Link>
                </div>
                
                {/* Desktop Navigation */}
                <div style={navContainerStyle}>
                    <nav style={navStyle}>
                        <Link 
                            to="/" 
                            style={linkStyle(isLinkActive('/'))}
                            onMouseEnter={(e) => handleLinkHover(e, isLinkActive('/'))}
                            onMouseLeave={(e) => handleLinkLeave(e, isLinkActive('/'))}
                            onClick={(e) => handleLinkClick(e, '/')}
                        >
                            Home
                            {isLinkActive('/') && 
                                <div 
                                    style={activeLinkIndicator}
                                    key={`indicator-${activeLink || 'default'}`}
                                ></div>
                            }
                        </Link>
                        <Link 
                            to="/about" 
                            style={linkStyle(isLinkActive('/about'))}
                            onMouseEnter={(e) => handleLinkHover(e, isLinkActive('/about'))}
                            onMouseLeave={(e) => handleLinkLeave(e, isLinkActive('/about'))}
                            onClick={(e) => handleLinkClick(e, '/about')}
                        >
                            About Us
                            {isLinkActive('/about') && 
                                <div 
                                    style={activeLinkIndicator}
                                    key={`indicator-${activeLink || 'default'}`}
                                ></div>
                            }
                        </Link>
                        <Link 
                            to="/products" 
                            style={linkStyle(isLinkActive('/products'))}
                            onMouseEnter={(e) => handleLinkHover(e, isLinkActive('/products'))}
                            onMouseLeave={(e) => handleLinkLeave(e, isLinkActive('/products'))}
                            onClick={(e) => handleLinkClick(e, '/products')}
                        >
                            Products
                            {isLinkActive('/products') && 
                                <div 
                                    style={activeLinkIndicator}
                                    key={`indicator-${activeLink || 'default'}`}
                                ></div>
                            }
                        </Link>
                        <Link 
                            to="/gallery" 
                            style={linkStyle(isLinkActive('/gallery'))}
                            onMouseEnter={(e) => handleLinkHover(e, isLinkActive('/gallery'))}
                            onMouseLeave={(e) => handleLinkLeave(e, isLinkActive('/gallery'))}
                            onClick={(e) => handleLinkClick(e, '/gallery')}
                        >
                            Gallery
                            {isLinkActive('/gallery') && 
                                <div 
                                    style={activeLinkIndicator}
                                    key={`indicator-${activeLink || 'default'}`}
                                ></div>
                            }
                        </Link>
                        <a 
                            href="https://deeceeelevators.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={linkStyle(false)}
                            onMouseEnter={(e) => handleLinkHover(e, false)}
                            onMouseLeave={(e) => handleLinkLeave(e, false)}
                        >
                            Old Website
                        </a>
                    </nav>
                </div>
                
                {/* CTA Button and Mobile Menu */}
                <div style={ctaContainerStyle}>
                    {!isMobile && (
                        <Link to="/contact">
                            <button 
                                style={buttonStyle}
                                onMouseEnter={(e) => {
                                    if (scrolled) {
                                        e.target.style.backgroundColor = primaryHoverColor;
                                    } else {
                                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                                    }
                                    e.target.style.transform = 'translateY(-3px)';
                                    e.target.style.boxShadow = scrolled 
                                        ? `0 6px 15px rgba(22, 59, 162, 0.4)`
                                        : '0 6px 15px rgba(0, 0, 0, 0.25)';
                                }}
                                onMouseLeave={(e) => {
                                    if (scrolled) {
                                        e.target.style.backgroundColor = primaryColor;
                                    } else {
                                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                                    }
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = scrolled
                                        ? `0 4px 10px rgba(22, 59, 162, 0.28)`
                                        : '0 4px 10px rgba(0, 0, 0, 0.15)';
                                }}
                                onClick={(e) => {
                                    e.target.style.transform = 'scale(0.95)';
                                    setTimeout(() => {
                                        e.target.style.transform = 'translateY(-3px)';
                                    }, 200);
                                    setActiveLink('/contact');
                                }}
                            >
                                Contact Us
                            </button>
                        </Link>
                    )}
                    
                    {/* Mobile Menu Button */}
                    <div 
                        style={hamburgerStyle} 
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        <span style={hamburgerLineStyle('top')}></span>
                        <span style={hamburgerLineStyle('middle')}></span>
                        <span style={hamburgerLineStyle('bottom')}></span>
                    </div>
                </div>
            </header>
            
            {/* Mobile Navigation - Always dark text on light background */}
            <nav style={mobileNavStyle}>
                <Link 
                    to="/" 
                    style={{...linkStyle(isLinkActive('/')), margin: '8px 0', fontSize: '16px', color: isLinkActive('/') ? primaryColor : textColorDark}}
                    onClick={(e) => {
                        handleLinkClick(e, '/');
                        setMobileMenuOpen(false);
                    }}
                >
                    Home
                    {isLinkActive('/') && 
                        <div 
                            style={activeLinkIndicator}
                            key={`indicator-mobile-${activeLink || 'default'}`}
                        ></div>
                    }
                </Link>
                <Link 
                    to="/about" 
                    style={{...linkStyle(isLinkActive('/about')), margin: '8px 0', fontSize: '16px', color: isLinkActive('/about') ? primaryColor : textColorDark}}
                    onClick={(e) => {
                        handleLinkClick(e, '/about');
                        setMobileMenuOpen(false);
                    }}
                >
                    About Us
                    {isLinkActive('/about') && 
                        <div 
                            style={activeLinkIndicator}
                            key={`indicator-mobile-${activeLink || 'default'}`}
                        ></div>
                    }
                </Link>
                <Link 
                    to="/products" 
                    style={{...linkStyle(isLinkActive('/products')), margin: '8px 0', fontSize: '16px', color: isLinkActive('/products') ? primaryColor : textColorDark}}
                    onClick={(e) => {
                        handleLinkClick(e, '/products');
                        setMobileMenuOpen(false);
                    }}
                >
                    Products
                    {isLinkActive('/products') && 
                        <div 
                            style={activeLinkIndicator}
                            key={`indicator-mobile-${activeLink || 'default'}`}
                        ></div>
                    }
                </Link>
                <Link 
                    to="/gallery" 
                    style={{...linkStyle(isLinkActive('/gallery')), margin: '8px 0', fontSize: '16px', color: isLinkActive('/gallery') ? primaryColor : textColorDark}}
                    onClick={(e) => {
                        handleLinkClick(e, '/gallery');
                        setMobileMenuOpen(false);
                    }}
                >
                    Gallery
                    {isLinkActive('/gallery') && 
                        <div 
                            style={activeLinkIndicator}
                            key={`indicator-mobile-${activeLink || 'default'}`}
                        ></div>
                    }
                </Link>
                <a 
                    href="https://deeceeelevators.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{...linkStyle(false), margin: '8px 0', fontSize: '16px', color: textColorDark}}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    Old Website
                </a>
                <Link to="/contact" style={{ width: '100%', marginTop: '10px' }}>
                    <button 
                        style={{...buttonStyle, width: '100%', padding: '12px', backgroundColor: primaryColor, color: 'white'}}
                        onClick={(e) => {
                            e.target.style.transform = 'scale(0.95)';
                            setTimeout(() => {
                                e.target.style.transform = 'scale(1)';
                                setMobileMenuOpen(false);
                            }, 200);
                            setActiveLink('/contact');
                        }}
                    >
                        Contact Us
                    </button>
                </Link>
            </nav>
            
            {/* Overlay for mobile menu backdrop */}
            {mobileMenuOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(22, 59, 162, 0.12)',
                        zIndex: 998,
                        opacity: mobileMenuOpen ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        backdropFilter: 'blur(3px)'
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}
        </>
    );
};

export default Header;